import { useEffect, useState } from 'react';
import cfac22 from 'js/library/utils/cfac22';
import { Link, useNavigate } from 'react-router-dom';

import { CircularProgress, Typography } from '@mui/material';

import {
  startMatomo,
  trackEventMatomoVisit,
  trackEventMatomoElementId,
  formatPostUrl,
  isFrameUrl,
} from 'js/library/utils/helpers';
import { useCurrentUserInfo } from 'js/library/services/currentUserInfo';
import * as S from './styles';

import { listSomePosts } from 'js/library/utils/API/listSomePosts';
import InfiniteScroll from 'react-infinite-scroll-component';
import LoadingLists from 'js/containers/Loading/LoadingLists';
import LoginModal from 'js/containers/Login/LoginModal';

const LIMIT = 10;

function extractPosts(response) {
  if (!response || typeof response !== 'object') return [];
  const unwrapped = response.response ?? response;
  if (Array.isArray(unwrapped)) return unwrapped;
  if (Array.isArray(unwrapped.posts)) return unwrapped.posts;
  if (Array.isArray(unwrapped.result)) return unwrapped.result;
  return [];
}

function extractPagination(response, posts = []) {
  const unwrapped = response?.response ?? response;
  const pagination = unwrapped?.pagination;
  if (pagination) {
    return {
      lastDateSender: Number(pagination.lastDateSender || 0),
      lastDateFile: Number(pagination.lastDateFile || 0),
      initialDateSender: Number(pagination.initialDateSender || 0),
      initialDateFile: Number(pagination.initialDateFile || 0),
    };
  }
  const dates = posts.map((p) => Number(p?.publishDate || 0)).filter((n) => n > 0);
  if (dates.length === 0) return null;
  return {
    lastDateSender: Math.max(...dates),
    lastDateFile: Math.max(...dates),
    initialDateSender: Math.min(...dates),
    initialDateFile: Math.min(...dates),
  };
}

// Função nativa para calcular tempo relativo em português
const timeAgo = (date) => {
  const now = new Date();
  const diffInMs = now - new Date(date);
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInMinutes < 1) return 'agora';
  if (diffInMinutes < 60) return `há ${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''}`;
  if (diffInHours < 24) return `há ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
  if (diffInDays < 30) return `há ${diffInDays} dia${diffInDays > 1 ? 's' : ''}`;
  if (diffInMonths < 12) return `há ${diffInMonths} ${diffInMonths > 1 ? 'meses' : 'mês'}`;
  return `há ${diffInYears} ano${diffInYears > 1 ? 's' : ''}`;
};

export default function Home() {
  const navigate = useNavigate();
  const { userInfo, loading: userInfoLoading } = useCurrentUserInfo();
  const userUId = userInfo?.uId;
  const userFeedKey = userUId || userInfo?.guestId || userInfo?.triiboId || null;
  const userOrgs = userInfo?.orgs;
  const userKeywords = userInfo?.keyWordsSetLimit;
  const isGuest = Boolean(userInfo?.triiboId?.includes('@guest'));

  const [feed, setFeed] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [feedLoading, setFeedLoading] = useState(true);
  const [isOpenModalLogin, setIsOpenModalLogin] = useState(false);
  const [pagination, setPagination] = useState({
    lastDateSender: 0,
    lastDateFile: 0,
    initialDateSender: 0,
    initialDateFile: 0,
  });

  useEffect(() => {
    startMatomo();
  }, [userInfo?.uId]);

  useEffect(() => {
    if (userInfoLoading) {
      return undefined;
    }

    if (!userFeedKey) {
      setFeed([]);
      setFeedLoading(false);
      setHasMore(false);
      return undefined;
    }

    let isActive = true;
    const now = Date.now();

    trackEventMatomoVisit('Home');
    document.title = 'Home';
    setFeed([]);
    setHasMore(true);
    setFeedLoading(true);

    const payload = {
      initialDateSender: now,
      initialDateFile: now,
      lastDateSender: 0,
      lastDateFile: 0,
      qtyPostsNew: 0,
      qtyPostsOld: LIMIT,
      orgs: userOrgs ?? [],
      keywords: userKeywords ?? [],
      relationships: [],
    };

    listSomePosts(userUId, payload)
      .then((response) => {
        if (!isActive) return;

        const posts = extractPosts(response);
        const nextPagination = extractPagination(response, posts);

        const formatted = posts.map((post) => ({
          ...post,
          postDate: timeAgo(post.publishDate ?? post.creationDate),
          postUrlRedirect: ActionButtonUrl(post),
        }));

        setFeed(formatted);
        setFeedLoading(false);
        if (nextPagination) setPagination(nextPagination);
        if (posts.length < LIMIT) setHasMore(false);
      })
      .catch((err) => {
        if (!isActive) return;

        console.error('Erro listSomePosts inicial:', err);
        setFeedLoading(false);
        setHasMore(false);
      });

    return () => {
      isActive = false;
    };
  }, [userFeedKey, userInfoLoading, userKeywords, userOrgs, userUId]);

  const GetMorePosts = async () => {
    if (!userInfo) return;

    const payload = {
      initialDateSender: pagination.initialDateSender || Date.now(),
      initialDateFile: pagination.initialDateFile || Date.now(),
      lastDateSender: 0,
      lastDateFile: 0,
      qtyPostsNew: 0,
      qtyPostsOld: LIMIT,
      orgs: userInfo?.orgs ?? [],
      keywords: userInfo?.keyWordsSetLimit ?? [],
      relationships: [],
    };

    listSomePosts(userInfo.uId, payload)
      .then((response) => {
        const newPosts = extractPosts(response);
        const nextPagination = extractPagination(response, newPosts);

        if (newPosts.length === 0) {
          setHasMore(false);
          return;
        }

        const formatted = newPosts.map((post) => ({
          ...post,
          postDate: timeAgo(post.publishDate ?? post.creationDate),
          postUrlRedirect: ActionButtonUrl(post),
        }));

        setFeed((prev) => [...prev, ...formatted]);
        if (nextPagination) setPagination(nextPagination);
        if (newPosts.length < LIMIT) setHasMore(false);
      })
      .catch((err) => {
        console.error('Erro listSomePosts paginação:', err);
        setHasMore(false);
      });
  };

  const ActionButtonUrl = (postInfos) => {
    const data = {
      action: postInfos.action.type,
      url: postInfos.appFilter.url,
      itemId: postInfos.appFilter.itemId,
      postId: postInfos.id,
    };

    const url = formatPostUrl(data);
    return url;
  };

  const handlePostActionClick = (event, post) => {
    const url = post.postUrlRedirect;

    if (post.appFilter?.itemId === 'directNavigation' || isFrameUrl(url)) {
      event.preventDefault();
      const urlRedirect = new URL(url, window.location.origin);
      navigate(urlRedirect.pathname + urlRedirect.search);
    }

    trackEventMatomoElementId('ListaDePosts', 'tocar', 'botão', post.id);
  };

  if (userInfoLoading) {
    return (
      <div className="loading-content" style={{ textAlign: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      {isOpenModalLogin && <LoginModal openState={isOpenModalLogin} />}
      {feedLoading ? (
        <div className="loading-content" style={{ textAlign: 'center' }}>
          <CircularProgress />
        </div>
      ) : feed.length === 0 ? (
        <div className="loading-content" style={{ textAlign: 'center' }}>
          <Typography variant="subtitle1" color="textSecondary">
            Não existem postagens disponíveis.
          </Typography>
        </div>
      ) : (
        <InfiniteScroll
          dataLength={feed.length}
          next={GetMorePosts}
          hasMore={hasMore}
          loader={<LoadingLists />}
        >
          <S.FeedContainer>
            {feed.map((post) => (
              <S.CardPost key={post.id}>
                <S.CardHeader>
                  <S.CardLogo
                    src={
                      cfac22('STORAGE_URL') + encodeURIComponent(post.sender.logo) + '?alt=media'
                    }
                  />
                  <h3>{post.sender.name}</h3>
                </S.CardHeader>
                <S.CardImage
                  src={cfac22('STORAGE_URL') + encodeURIComponent(post.image) + '?alt=media'}
                />
                {post.action && (
                  <>
                    {post.action.type === 'url' ||
                    post.action.type === 'browser' ||
                    post.action.type === 'webview' ? (
                      isGuest ? (
                        <S.CardActionButton
                          style={{
                            marginLeft: 'auto',
                          }}
                          onClick={() => {
                            trackEventMatomoElementId('ListaDePosts', 'tocar', 'botão', post.id);
                            setIsOpenModalLogin(true);
                          }}
                        >
                          {post.action.label}
                        </S.CardActionButton>
                      ) : (
                        <a
                          href={post.postUrlRedirect}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(event) => handlePostActionClick(event, post)}
                          style={{
                            marginLeft: 'auto',
                          }}
                        >
                          <S.CardActionButton>
                            {post.action.label}
                          </S.CardActionButton>
                        </a>
                      )
                    ) : (
                      <Link
                        to={{
                          pathname: post.postUrlRedirect.split('?')[0],
                          search: post.postUrlRedirect.split('?')[1],
                        }}
                        state={post}
                        style={{
                          marginLeft: 'auto',
                        }}
                      >
                        <S.CardActionButton
                          onClick={() =>
                            trackEventMatomoElementId('ListaDePosts', 'tocar', 'botão', post.id)
                          }
                        >
                          {post.action.label}
                        </S.CardActionButton>
                      </Link>
                    )}
                  </>
                )}

                <S.CardContent>
                  <h3>{post.title}</h3>
                  <S.Description>{post.message}</S.Description>
                  <span>{post.postDate}</span>
                </S.CardContent>
              </S.CardPost>
            ))}
          </S.FeedContainer>
        </InfiniteScroll>
      )}
    </>
  );
}
